import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { SectionHeader } from "@/components/SectionHeader";
import { contact } from "@/data/site";

const chapters = [
  { number: "01", title: "Industrial-grade support", text: "Reliable project support for industrial, refinery, infrastructure, EPC, and construction environments where delays are expensive." },
  { number: "02", title: "Commercially practical", text: "Competitive terms, clear coordination, and responsive communication keep procurement, operations, and site teams aligned." },
  { number: "03", title: "Execution without drift", text: "From timely mobilization to disciplined demobilization, every engagement is managed around safety, quality, and schedule certainty." },
];

export function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 45%"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 80, damping: 24 });

  return (
    <section id="about" className="border-b border-industrial-line py-24 sm:py-32" data-testid="about-section">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
        <SectionHeader eyebrow="About / 01" title="Built for projects that cannot pause" description="Shyam Equipment and Infratech is a PAN India service partner focused on dependable manpower, well-maintained equipment, and professional transaction support." testId="about-section-header" />
        <div ref={ref} className="relative grid gap-10 lg:grid-cols-[120px_1fr]">
          <div className="relative hidden lg:block">
            <div className="absolute left-1/2 top-0 h-full w-px bg-industrial-line" />
            <motion.div style={{ scaleY }} className="absolute left-1/2 top-0 h-full w-px origin-top bg-industrial-yellow" data-testid="about-scroll-timeline" />
          </div>
          <div className="grid gap-6">
            {chapters.map((chapter, index) => (
              <motion.article key={chapter.number} initial={{ opacity: 0, x: 36 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.12, duration: 0.65 }} className="group grid gap-5 border border-industrial-line bg-industrial-surface p-6 transition-transform hover:translate-x-1 sm:grid-cols-[120px_1fr] sm:p-8" data-testid={`about-chapter-${chapter.number}`}>
                <p className="font-mono text-5xl font-semibold text-industrial-yellow/80 transition-colors group-hover:text-industrial-yellow">{chapter.number}</p>
                <div>
                  <h3 className="font-heading text-2xl font-semibold uppercase text-industrial-text">{chapter.title}</h3>
                  <p className="mt-3 max-w-3xl text-sm leading-7 text-industrial-muted sm:text-base">{chapter.text}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
        <div className="mt-10 flex flex-col items-start justify-between gap-5 border border-industrial-line bg-industrial-surface p-6 sm:flex-row sm:items-center sm:p-8" data-testid="about-profile-download-panel">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-industrial-yellow">Company Profile</p>
            <p className="mt-2 max-w-xl text-sm leading-6 text-industrial-muted">Share our capabilities with your procurement team — services, workforce categories, equipment fleet, and contact details in one document.</p>
          </div>
          <a href={contact.profilePdf} download="Shyam-Equipment-Infratech-Company-Profile.pdf" data-testid="about-download-profile-button" className="inline-flex shrink-0 items-center gap-3 bg-industrial-yellow px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-industrial-black transition-transform hover:-translate-y-0.5">
            Download Profile (PDF) <span aria-hidden="true">↓</span>
          </a>
        </div>
      </div>
    </section>
  );
}
